import useFilteredVariantTraits from "@insite/client-framework/Common/Hooks/useFilteredVariantTraits";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import closeVariantModal from "@insite/client-framework/Store/Components/ProductSelector/Handlers/CloseVariantModal";
import updateVariantSelection from "@insite/client-framework/Store/Components/ProductSelector/Handlers/UpdateVariantSelection";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState, getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setProduct from "@tSurya/Handlers/ProductSelector/SetProduct";
import { COLORS } from "@tSurya/theme/design";
import { TYPOGRAPHY } from "@tSurya/theme/design.surya";
import { isSwatchProduct, SWATCH_VARIANT_TRAIT_NAME } from "@tSurya/Common/Utilities/swatchUtils";
import { isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    skipInventoryValidation?: boolean;
    extendedStyles?: ProductSelectorVariantModalStyles;
    /**
     * When true, displays the parent product name (title) right above the select fields.
     * Defaults to false.
     */
    showProductTitle?: boolean;
    /**
     * Called when the user confirms a variant selection. Return `false` to defer back to
     * the modal's default ProductSelector resolution flow (this is how the global Modals
     * swatch handler lets non-swatch selections - e.g. the cart Quick Add - keep working);
     * returning anything else is treated as handled and closes the modal.
     */
    onSelect?: (product: ProductModel, productInfo: ProductInfo, parentProduct?: ProductModel) => void | false | Promise<void | false>;
}

const mapStateToProps = (state: ApplicationState) => {
    const { variantModalIsOpen, variantModalProductId, variantSelection, selectedVariantProductInfo } = getProductSelector(state);
    return {
        variantModalIsOpen,
        variantParentProduct: getProductState(state, variantModalProductId).value,
        variantChildren: getVariantChildrenDataView(state, variantModalProductId).value,
        selectedVariant: getProductState(state, selectedVariantProductInfo?.productId).value,
        selectedVariantProductInfo,
        variantSelection,
        currentPage: getCurrentPage(state),
    };
};

const mapDispatchToProps = {
    closeVariantModal,
    updateVariantSelection,
    setProduct,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorVariantModalStyles {
    modal?: ModalPresentationProps;
    wrapper?: InjectableCss;
    productTitle?: TypographyPresentationProps;
    variantSelect?: SelectProps;
    selectButton?: ButtonPresentationProps;
}

export const productSelectorVariantModalStyles: ProductSelectorVariantModalStyles = {
    modal: {
        size: 400,
        cssOverrides: {
            modalContainer: css`
                align-items: flex-start;
            `,
            modalTitle: css`
                padding: 10px 20px;
                font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                font-weight: ${TYPOGRAPHY.weight.bold} !important;
                font-size: ${TYPOGRAPHY.size.larger} !important;
                line-height: ${TYPOGRAPHY.lineHeight.lmedium} !important;
                font-style: normal;

                h1,
                h2,
                h3,
                h4,
                h5,
                h6 {
                    font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                    font-weight: ${TYPOGRAPHY.weight.bold} !important;
                    font-size: ${TYPOGRAPHY.size.larger} !important;
                    line-height: ${TYPOGRAPHY.lineHeight.lmedium} !important;
                    font-style: normal;
                }
            `,
            modalContent: css`
                padding: 20px;
                font-family: ${TYPOGRAPHY.fontFamily.title} !important;
            `,
        },
    },
    productTitle: {
        variant: "h6",
        css: css`
            margin-bottom: 12px;
            font-family: ${TYPOGRAPHY.fontFamily.title} !important;
        `,
    },
    variantSelect: {
        labelPosition: "left",
        labelProps: {
            css: css`
                width: 100%;
                text-align: left;
                padding: 0px 0px 0px 1px;
            `,
        },
        cssOverrides: {
            formField: css`
                margin-bottom: 10px;
                display: block;
            `,
            formInputWrapper: css`
                width: 100%;
            `,
        },
    },
    selectButton: {
        css: css`
            width: 100%;
            margin-top: 10px;
            border: 1px solid transparent;
            ${() =>
                isMgbwWebsite()
                    ? css`
                          &:hover {
                              background-color: #fff !important;
                              color: #323132 !important;
                              border: 1px solid #323132 !important;
                              > span {
                                  color: #323132 !important;
                              }
                          }
                      `
                    : css`
                          &:hover {
                              background-color: ${COLORS.Matterhorn};
                          }
                      `}
        `,
    },
};

/**
 * @deprecated Use productSelectorVariantModalStyles instead.
 */
export const quickOrderVariantModalStyles = productSelectorVariantModalStyles;

const ProductSelectorVariantModal = ({
    variantModalIsOpen,
    variantParentProduct,
    variantChildren,
    selectedVariant,
    variantSelection,
    extendedStyles,
    closeVariantModal,
    updateVariantSelection,
    setProduct,
    selectedVariantProductInfo,
    skipInventoryValidation,
    currentPage,
    onSelect,
    showProductTitle = false,
}: Props) => {
    const [styles] = useState(() => mergeToNew(quickOrderVariantModalStyles, extendedStyles));

    if (!variantParentProduct?.variantTraits || !variantChildren) {
        return null;
    }

    const closeModalHandler = () => {
        closeVariantModal();
    };

    const variantChangeHandler = (traitValueId: string, variantTraitId: string) => {
        updateVariantSelection({ traitValueId, variantTraitId });
    };

    const selectButtonClickHandler = () => {
        // Swatch add-to-cart is supplied by the caller via onSelect (the global Modals
        // swatch handler, or the configurator). onSelect returns false for non-swatch
        // selections so the cart Quick Add and other ProductSelector flows fall through
        // to the default resolution below unchanged.
        if (onSelect && selectedVariant && selectedVariantProductInfo) {
            const handled = onSelect(selectedVariant, selectedVariantProductInfo, variantParentProduct);
            if (handled !== false) {
                closeVariantModal();
                return;
            }
        }

        setProduct({
            productId: variantParentProduct.id,
            variantId: selectedVariant?.id,
            validateProduct: true,
            skipInventoryValidation,
        });
        closeVariantModal();
    };

    const filteredVariantTraits = useFilteredVariantTraits(variantParentProduct.variantTraits, variantChildren, variantSelection);

    // Swatch products use a dedicated technical StyleTrait (MGBWFabricSwatchVariantTrait) whose
    // Name/NameDisplay is the raw identifier (no translation entry exists), so the dropdown
    // label would show "MGBWFabricSwatchVariantTrait" instead of a friendly "Size" label.
    // Fall back to "Size" for the swatch trait to match the PDP treatment.
    const isSwatch = isSwatchProduct(variantParentProduct);
    const getTraitLabel = (traitNameDisplay: string, traitName: string): string =>
                isSwatch && traitName === SWATCH_VARIANT_TRAIT_NAME
                        ? translate("Size")
                        : traitNameDisplay === translate("Select Size")
                            ? translate("Size")
                            : traitNameDisplay;
    const getTraitInstruction = (traitLabel: string): string =>
        traitLabel === translate("Size") ? translate("Select a Size") : `${translate("Select")} ${traitLabel}`;

    return (
        <Modal
            {...styles.modal}
            headline={translate("Select Options")}
            isOpen={variantModalIsOpen}
            handleClose={closeModalHandler}
            data-test-selector="modal_ProductSelectorVariantModal"
        >
            <StyledWrapper {...styles.wrapper}>
                {showProductTitle && variantParentProduct.productTitle ? (
                    <Typography {...styles.productTitle} as="p" data-test-selector="productSelectorVariantModal_productTitle">
                        {variantParentProduct.productTitle}
                    </Typography>
                ) : null}
                {filteredVariantTraits.map(variantTrait => {
                    const traitLabel = getTraitLabel(variantTrait.nameDisplay, variantTrait.name);
                    const traitInstruction = getTraitInstruction(traitLabel);
                    const isSizeTrait = traitLabel === translate("Size");
                    return (
                        <Select
                            {...styles.variantSelect}
                            key={variantTrait.id.toString()}
                            label={traitInstruction}
                            value={variantSelection[variantTrait.id]}
                            onChange={event => variantChangeHandler(event.currentTarget.value, variantTrait.id)}
                            data-test-selector={`productSelectorVariantModal_Dropdown_${variantTrait.nameDisplay}`}
                        >
                            <option value="">
                                {isSizeTrait ? traitLabel : variantTrait.unselectedValue || traitInstruction}
                            </option>
                            {variantTrait.traitValues
                                ?.filter(item => !item.valueDisplay.toLowerCase().includes("custom"))
                                .map(traitValue => (
                                    <option value={`${traitValue.id}`} key={`${traitValue.id}`}>
                                        {traitValue.valueDisplay}
                                    </option>
                                ))}
                        </Select>
                    );
                })}
            </StyledWrapper>
            {currentPage.name !== "Freight Estimator" && selectedVariant && selectedVariantProductInfo ? (
                <ProductPrice product={{ product: selectedVariant, productInfo: selectedVariantProductInfo }} showLabel={false} />
            ) : null}
            <Button
                {...styles.selectButton}
                disabled={!selectedVariant || !selectedVariant.id}
                onClick={selectButtonClickHandler}
                data-test-selector="productSelectorVariantModal_selectButton"
            >
                {translate("Select")}
            </Button>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSelectorVariantModal);
